/**
 * AuthContext — Supabase Auth + Profile Provider
 *
 * Replaces: useAuthStore (regular users) + useRootAuthStore (superAdmin)
 * Old path:  secureLocalStorage/CryptoJS → JWT → Rails /session endpoint
 * New path:  Supabase Auth SDK → PostgreSQL users table (profile fetch)
 *
 * Two-layer model:
 * - `user`:    Supabase Auth User (id, email, app_metadata)
 * - `profile`: Full users table row (first_name, last_name, base_role, org_id, details, etc.)
 *
 * The Supabase SDK handles session persistence (IndexedDB), token refresh,
 * and cross-tab sync automatically — no manual storage management needed.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { toast } from 'react-toastify';
import { isCancelledError } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { identifySentryUser, clearSentryUser } from '../services/sentry';
import { queryClient } from '../services/queryClient';
import { PROFILE_STALE_MS, profileQueryKey } from '../services/query/lookupCache';
import type { Enums, Tables } from '../services/database.types';

/**
 * The account-status enum on the `users` row (`user_status`):
 * 'active' | 'inactive' | 'suspended' | 'draft' | 'archived'. Used to gate
 * blocked-account login — see BLOCKED_STATUSES below.
 */
type UserStatus = Enums<'user_status'>;

/**
 * The exact row shape fetchProfile() reads back: the full `users` row plus the
 * embedded organization + students FK lookups. Typing this lets the
 * account-blocking gate read `.status` as the real `user_status` enum instead
 * of `(data as any).status`.
 */
type ProfileQueryRow = Tables<'users'> & {
  organizations: { name: string; custom_fields: Record<string, string> | null } | null;
  students: { id: string } | { id: string }[] | null;
};

/**
 * Statuses that block app access. Phase 1 user management (2026-05-14):
 *  - 'archived' — end-of-year cleanup; record preserved but no login
 *  - 'suspended' — admin-imposed block (discipline, payment, etc.)
 *  - 'inactive' — legacy; kept here so existing data still blocks correctly
 *
 * Enforced in two places below: signIn() rejects upfront so the login form
 * shows the error inline, and fetchProfile() catches mid-session status
 * changes by signing the user out when their loaded profile is blocked.
 *
 * This is client-side enforcement only. Server-side narrowing (RLS) is
 * tracked separately as the Phase B RLS tightening work — that pass will
 * also reject queries from a blocked user's JWT.
 */
const BLOCKED_STATUSES = new Set<UserStatus>(['archived', 'suspended', 'inactive']);

function isBlockedStatus(status: UserStatus | null | undefined): status is UserStatus {
  return !!status && BLOCKED_STATUSES.has(status);
}

function blockedStatusMessage(status: UserStatus): string {
  if (status === 'archived') return 'This account has been archived. Please contact your administrator.';
  if (status === 'suspended') return 'This account is suspended. Please contact your administrator.';
  return 'This account is inactive. Please contact your administrator.';
}

async function readProfile(authUserId: string): Promise<UserProfile> {
  const { data, error } = await supabase
    .from('users')
    .select('*, organizations!users_org_id_fkey(name, custom_fields), students!students_user_id_fkey(id)')
    .eq('auth_user_id', authUserId)
    .single<ProfileQueryRow>();

  if (error) throw error;

  const { organizations, students, ...row } = data;
  const studentRow = Array.isArray(students) ? students[0] : students;

  return {
    ...row,
    organization: organizations ?? null,
    student_id: studentRow?.id ?? null,
  };
}

type AccessRow = Pick<Tables<'users'>, 'status' | 'base_role' | 'user_role_id'>;

async function readAccess(authUserId: string): Promise<AccessRow | null> {
  const { data, error } = await supabase
    .from('users')
    .select('status, base_role, user_role_id')
    .eq('auth_user_id', authUserId)
    .single<AccessRow>();

  return error ? null : data;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * UserProfile — Full user record from the `users` table.
 * This is the shape that 59+ consumer files expect when they access
 * `user.first_name`, `user.details`, `user.organization`, etc.
 */
export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  base_role: string;
  org_id: string;
  image?: string | null;
  /**
   * Public URL of the profile photo in the `avatars` storage bucket
   * (users.avatar_url). Null/absent = no photo; consumers fall back to
   * stringAvatar initials. Carried through the optimistic updateProfile
   * spread so the header/profile pages refresh without a re-fetch.
   */
  avatar_url?: string | null;
  status: UserStatus;
  id_number?: string | null;
  user_name?: string | null;
  created_at?: string;
  updated_at?: string;
  details?: Record<string, any> | null;
  organization?: { name: string; custom_fields?: Record<string, string> | null } | null;
  /**
   * Resolved row id from the `students` table when this profile is a
   * student. `profile.id` is the `users.id`, NOT what FK columns like
   * `course_enrollments.student_id` expect (those reference `students.id`).
   * Anywhere a student-side query needs the FK target, use `student_id`.
   * Null for non-student profiles.
   */
  student_id?: string | null;
  [key: string]: any;
}

export interface AuthContextValue {
  // Supabase auth identity
  session: Session | null;
  user: User | null;

  // Full database profile (fetched from users table after auth)
  profile: UserProfile | null;

  // Derived convenience fields
  orgId: string | null;
  baseRole: string | null;
  accessToken: string | null;

  // State
  loading: boolean;

  // Auth actions
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;

  // Profile management
  refreshProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const profileOwner = useRef<string | null>(null);

  const forgetCachedUser = useCallback(() => {
    profileOwner.current = null;
    queryClient.clear();
  }, []);

  const fetchProfile = useCallback(async (userId: string, fresh = false) => {
    const key = profileQueryKey(userId);

    const signOutIfBlocked = (status: UserStatus | null | undefined) => {
      if (!isBlockedStatus(status)) return false;

      toast.error(blockedStatusMessage(status));
      setProfile(null);
      void supabase.auth.signOut();

      return true;
    };

    if (profileOwner.current !== userId) {
      if (profileOwner.current !== null) void queryClient.resetQueries();

      profileOwner.current = userId;
    }

    if (fresh) queryClient.removeQueries({ queryKey: key });

    try {
      const cached = queryClient.getQueryData<UserProfile>(key);
      const cachedIsFresh = queryClient.getQueryCache().find({ queryKey: key })?.isStaleByTime(PROFILE_STALE_MS) === false;

      if (cached && cachedIsFresh) {
        const access = await readAccess(userId);

        if (profileOwner.current !== userId) return;

        if (access && signOutIfBlocked(access.status)) return;

        if (access && (access.base_role !== cached.base_role || access.user_role_id !== cached.user_role_id)) {
          queryClient.removeQueries({ queryKey: key });
        }
      }

      const loaded = await queryClient.fetchQuery({
        queryKey: key,
        queryFn: () => readProfile(userId),
        staleTime: PROFILE_STALE_MS,
        retry: false,
      });

      if (profileOwner.current !== userId || signOutIfBlocked(loaded.status)) return;

      setProfile(loaded);
      void identifySentryUser({
        id: loaded.id,
        org_id: loaded.org_id,
        base_role: loaded.base_role,
      });
    } catch (err) {
      if (isCancelledError(err)) return;

      console.error('Profile fetch error:', err);

      if (profileOwner.current === userId) setProfile(null);
    }
  }, []);

  useEffect(() => {
    // Use onAuthStateChange exclusively for session initialization.
    // Supabase emits INITIAL_SESSION synchronously from storage (no network call),
    // so setLoading(false) fires immediately — no waiting for getSession() round-trips.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);

        if (event === 'INITIAL_SESSION') {
          // First event always fires from storage — unblock routing immediately.
          setLoading(false);
          if (newSession?.user) {
            fetchProfile(newSession.user.id); // fire-and-forget
          }
        } else if (event === 'SIGNED_IN' && newSession?.user) {
          fetchProfile(newSession.user.id); // fire-and-forget
        } else if (event === 'PASSWORD_RECOVERY') {
          // A "set your password" recovery link was clicked. Supabase has opened
          // a short recovery session; route to the set-password page regardless
          // of where the link landed — some Supabase redirect-allowlist configs
          // drop the path and fall back to the site root. Guard against a loop
          // when already there. The token is already consumed into the persisted
          // session, so dropping the hash on replace is fine (SetPasswordPage
          // reads the session).
          if (typeof window !== 'undefined' && window.location.pathname !== '/set-password') {
            window.location.replace('/set-password');
          }
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
          forgetCachedUser();
          void clearSentryUser();
        }
        // TOKEN_REFRESHED: setSession already updated; no profile re-fetch needed
      },
    );

    // Safety net: if INITIAL_SESSION never fires, release loading after 5s.
    const safety = setTimeout(() => setLoading(false), 5_000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(safety);
    };
  }, [fetchProfile, forgetCachedUser]);

  // ---------------------------------------------------------------------------
  // Auth actions
  // ---------------------------------------------------------------------------

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return { error: error as Error };
    }

    // Status gate. We just got an auth.users session; the public.users
    // profile may say archived/suspended/inactive in which case we must
    // immediately reject the login. Without this check the SIGNED_IN
    // event would route them in before fetchProfile catches the block.
    if (data?.user?.id) {
      const { data: profileRow } = await supabase
        .from('users')
        .select('status')
        .eq('auth_user_id', data.user.id)
        .single<Pick<Tables<'users'>, 'status'>>();

      const status = profileRow?.status ?? null;
      if (isBlockedStatus(status)) {
        await supabase.auth.signOut();
        return { error: new Error(blockedStatusMessage(status)) };
      }
    }

    return { error: null };
  }, []);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (err) {
      console.error('Sign out error:', err);
    } finally {
      // Always clear local state, even if the remote sign-out call failed —
      // otherwise a network/API error leaves the user stuck signed in.
      setProfile(null);
      forgetCachedUser();
    }
  }, [forgetCachedUser]);

  // ---------------------------------------------------------------------------
  // Profile management
  // ---------------------------------------------------------------------------

  /** Re-fetch profile from database (e.g., after profile edit) */
  const refreshProfile = useCallback(async () => {
    if (session?.user?.id) {
      await fetchProfile(session.user.id, true);
    }
  }, [session?.user?.id, fetchProfile]);

  /** Optimistic profile update (patch local state without re-fetch) */
  const updateProfile = useCallback((updates: Partial<UserProfile>) => {
    setProfile(prev => (prev ? { ...prev, ...updates } : null));

    if (profileOwner.current) {
      queryClient.setQueryData<UserProfile>(profileQueryKey(profileOwner.current), (cached) =>
        cached ? { ...cached, ...updates } : cached,
      );
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const user = session?.user ?? null;
  const orgId = profile?.org_id ?? user?.app_metadata?.org_id ?? null;
  const baseRole = profile?.base_role ?? user?.app_metadata?.base_role ?? null;
  const accessToken = session?.access_token ?? null;

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        orgId,
        baseRole,
        accessToken,
        loading,
        signIn,
        signOut,
        refreshProfile,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
